import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Card,
  CardContent,
  CardMedia,
  Skeleton,
  Grid,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";

const CompactCard = styled(Card)(({ highlight }) => ({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  borderRadius: 12,
  minHeight: 72,
  border: highlight ? "2px solid #4caf50" : "1px solid #c9e7e0",
  background: highlight
    ? "linear-gradient(90deg, #e0f7fa 0%, #e8f5e9 100%)"
    : "#fcfcfd",
  boxShadow: "0 2px 12px 0 rgba(44, 62, 80, .04)",
  transition: "0.16s box-shadow, 0.16s border, 0.16s background",
  marginBottom: 1,
  "&:hover": {
    background: "linear-gradient(90deg, #e0f2fe 0%, #e0f3f1 100%)",
    boxShadow: "0 6px 22px 0 rgba(44, 62, 80, .08)",
    borderColor: "#189ab4",
  },
}));

const CompactMedia = styled(CardMedia)({
  width: 56,
  height: 56,
  borderRadius: 6,
  objectFit: "contain",
  marginLeft: 12,
  background: "#f8fafb",
  border: "1px solid #e0f2f1",
});

const CompactContent = styled(CardContent)({
  flex: 1,
  minWidth: 0,
  padding: 12,
  "&:last-child": { paddingBottom: 12 },
});

// Main component
const SubstituteDrugsPage = ({ item }) => {
  const [data, setData] = useState([]);
  const [visibleCount, setVisibleCount] = useState(10);
  const [loading, setLoading] = useState(false);

  const fetchSubstitutes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        "https://api.medstown.com/api/medshubs/substitute-drugs",
        {
          params: {
            short_composition: item.short_composition,
            skip_id: item.sku_id,
          },
        }
      );
      const resData = response?.data?.data;
      const formatted = Array.isArray(resData) ? resData : resData.list || [];
      setData(formatted);
    } catch (err) {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubstitutes();
    setVisibleCount(10);
  }, [item.short_composition, item.sku_id]);

  const displayedItems = data.slice(0, visibleCount);
  const showMore = visibleCount < data.length;

  return (
    <Box px={1} py={2}>
      <Typography
        variant="subtitle1"
        fontWeight={700}
        sx={{
          mb: 1.5,
          fontSize: 17,
          letterSpacing: 0.2,
          color: "#11525A",
        }}
      >
        Substitutes ({data.length})
      </Typography>

      {loading ? (
        <Grid container spacing={1.5}>
          {Array.from({ length: 6 }).map((_, idx) => (
            <Grid item xs={12} sm={6} key={idx}>
              <Skeleton variant="rounded" height={68} />
            </Grid>
          ))}
        </Grid>
      ) : data.length === 0 ? (
        <Box textAlign="center" py={5} sx={{ color: "#aaa" }}>
          <Typography variant="body2" fontSize={15}>
            No substitutes found
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={1.5}>
          {displayedItems.map((drug, idx) => (
            <Grid item xs={12} sm={6} key={drug.id || idx}>
              <CompactCard
                highlight={!!drug.cnt}
                onClick={() =>
                  window.open(
                    `https://www.dashboard.medstown.com/admin/durgs-individual-page/${drug._id || drug.id}`,
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
                tabIndex={0}
                role="button"
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    window.open(
                      `https://www.dashboard.medstown.com/admin/durgs-individual-page/${drug._id || drug.id}`,
                      "_blank"
                    );
                  }
                }}
              >
                <CompactMedia
                  component="img"
                  image={
                    drug.image_url ||
                    drug?.detailJson?.sku?.images?.[0]?.thumbnail ||
                    "/no-image.png"
                  }
                  alt={drug.name}
                />
                <CompactContent>
                  <Box
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "#004d4d",
                      mb: 0.2,
                      cursor: "pointer",
                      // textDecoration: "underline dotted",
                      "&:hover": { color: "#08798c" },
                      transition: "color 0.18s",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={drug.name}
                    onClick={e => {
                      e.stopPropagation();
                      window.open(
                        `https://www.dashboard.medstown.com/admin/durgs-individual-page/${drug._id || drug.id}`,
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                  >
                    {drug.name}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: 13, lineHeight: 1.5 }}
                  >
                    {drug.pack_size_label || "No pack info"}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#009688",
                        fontWeight: 600,
                        fontSize: 13,
                        mr: 0.6,
                        letterSpacing: "0.01em",
                      }}
                    >
                      ₹{drug.price ?? "--"}
                    </Typography>
                    {drug.cnt > 0 && (
                      <Chip
                        label="In Stock"
                        color="success"
                        size="small"
                        sx={{
                          fontSize: 11,
                          height: 19,
                          minWidth: 0,
                          px: 1,
                          bgcolor: "rgba(76,175,80,0.05)",
                        }}
                      />
                    )}
                  </Box>
                </CompactContent>
              </CompactCard>
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && data.length > 10 && (
        <Box textAlign="center" mt={1.5}>
          <Button
            variant="contained"
            size="small"
            sx={{
              minWidth: 84,
              fontSize: 13,
              letterSpacing: 0.1,
              py: 0.7,
              px: 2,
              mt: 0.2,
              background: "linear-gradient(91deg, #1ea4a0 0%, #119da4 100%)",
              borderRadius: 8,
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
            }}
            onClick={() =>
              setVisibleCount(prev => (showMore ? data.length : 10))
            }
          >
            {showMore ? "Show All" : "Show Less"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SubstituteDrugsPage;
